import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { ENTITY_LABEL } from "../../lib/plans";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  business_name: "",
  uses_xero: false,
  prior_year_catchup: false,
  notes: "",
};

export default function CheckoutDialog({ open, onOpenChange, selected }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [fyInfo, setFyInfo] = useState(null);
  // If the redirect-to-Stripe doesn't take effect (slow network, popup blocker
  // extension, etc.) we surface a manual "Open Stripe checkout" link so the
  // visitor never sees a permanently-frozen button.
  const [stripeUrl, setStripeUrl] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setSubmitting(false);
      setStripeUrl(null);
    }
  }, [open]);

  // While the dialog is open, forward every wheel/touchmove on the page to the
  // dialog's internal scroll container — even when the cursor is outside the
  // dialog (e.g. on the dim backdrop). This avoids the "scroll only works on
  // the inner scrollbar" issue caused by Lenis/body-lock and keeps the
  // experience natural.
  useEffect(() => {
    if (!open) return;
    let lastTouchY = null;

    const onWheel = (e) => {
      const el = scrollRef.current;
      if (!el) return;
      // If the wheel event target is a form control inside the dialog (e.g.
      // textarea that internally scrolls), let it handle natively.
      if (el.contains(e.target)) {
        const tag = (e.target.tagName || "").toUpperCase();
        if (tag === "TEXTAREA") return;
      }
      e.preventDefault();
      el.scrollTop += e.deltaY;
    };

    const onTouchStart = (e) => {
      lastTouchY = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e) => {
      const el = scrollRef.current;
      if (!el || lastTouchY == null) return;
      const y = e.touches[0]?.clientY ?? lastTouchY;
      const dy = lastTouchY - y;
      lastTouchY = y;
      // Only intercept when the touch is outside the dialog content; native
      // touch scrolling inside the dialog already works.
      if (!el.contains(e.target)) {
        e.preventDefault();
        el.scrollTop += dy;
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [open]);

  useEffect(() => {
    // Fetch FY info once per mount
    axios
      .get(`${API}/fy-info`)
      .then((r) => setFyInfo(r.data))
      .catch(() => {});
  }, []);

  const plan = selected?.plan;
  const entity = selected?.entity;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!/.+@.+\..+/.test(form.email)) return "Please enter a valid email.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    if (!plan || !entity) return;

    setSubmitting(true);
    setStripeUrl(null);
    try {
      const { data } = await axios.post(
        `${API}/checkout/session`,
        {
          origin_url: window.location.origin,
          lead: {
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || null,
            business_name: form.business_name.trim() || null,
            entity,
            uses_xero: !!form.uses_xero,
            plan_id: plan.id,
            prior_year_catchup: !!form.prior_year_catchup,
            notes: form.notes.trim() || null,
          },
        },
        // 25s ceiling — beyond this we surface a real error rather than
        // letting the button hang at "Redirecting to Stripe…" indefinitely.
        { timeout: 25000 },
      );
      if (data?.url) {
        // Show the manual fallback link before navigating. If the assign()
        // succeeds (the common case) the page unloads and the user never sees
        // it. If it doesn't (popup blocker, slow Stripe load, redirect blocked
        // by an extension) the user has a visible button to click.
        setStripeUrl(data.url);
        window.location.assign(data.url);
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detail ||
        (e?.code === "ECONNABORTED"
          ? "Stripe took too long to respond. Please try again."
          : e?.message) ||
        "Something went wrong. Please try again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={scrollRef}
        className="sm:max-w-[560px] rounded-none border-slate-200 p-0 max-h-[92vh] overflow-y-auto"
        data-testid="checkout-dialog"
      >
        <div className="bg-[#0A192F] text-white px-6 py-5">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              {plan ? `Start with ${plan.name}` : "Get started"}
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              {plan
                ? `${ENTITY_LABEL[entity]} · $${plan.price} + GST NZD / month`
                : "Pick a plan to continue"}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-6 py-6 space-y-4" data-testid="checkout-form">
          <Field label="Full name" required>
            <Input
              data-testid="checkout-input-name"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349]"
              placeholder="Jane Cooper"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" required>
              <Input
                data-testid="checkout-input-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349]"
                placeholder="jane@business.co.nz"
              />
            </Field>
            <Field label="Phone">
              <Input
                data-testid="checkout-input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349]"
                placeholder="021 000 0000"
              />
            </Field>
          </div>
          <Field label={entity === "company" ? "Company name" : "Business / trading name"}>
            <Input
              data-testid="checkout-input-business"
              value={form.business_name}
              onChange={(e) => update("business_name", e.target.value)}
              className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349]"
              placeholder={entity === "company" ? "Cooper & Co Limited" : "Jane Cooper Design"}
            />
          </Field>

          {entity === "company" ? (
            <div className="flex items-start gap-3 border border-slate-200 p-4">
              <Checkbox
                id="uses_xero"
                data-testid="checkout-input-xero"
                checked={form.uses_xero}
                onCheckedChange={(v) => update("uses_xero", !!v)}
                className="mt-0.5 rounded-none border-slate-400 data-[state=checked]:bg-[#C9A349] data-[state=checked]:border-[#C9A349]"
              />
              <div>
                <Label htmlFor="uses_xero" className="font-heading font-bold text-sm cursor-pointer">
                  I already use Xero
                </Label>
                <p className="text-xs text-slate-500 mt-1">
                  Companies run on Xero. You'll need your own paid Xero subscription (it's not
                  included in our pricing). Not on Xero yet? Leave this unchecked — we'll help you
                  pick a plan.
                </p>
              </div>
            </div>
          ) : (
            <div
              className="flex items-start gap-3 border border-slate-200 bg-slate-50 p-4"
              data-testid="checkout-prosaic-note"
            >
              <div className="mt-0.5 w-4 h-4 bg-[#C9A349]" aria-hidden />
              <div>
                <div className="font-heading font-bold text-sm">
                  Sole traders use Prosaic
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  No Xero file needed, and no extra subscription to pay. We set up Prosaic for
                  you and cover the subscription cost — it's included in your plan.
                </p>
              </div>
            </div>
          )}

          {/* Financial year coverage + prior-year catch-up */}
          {fyInfo && plan && (
            <div
              className="border border-[#0A192F] bg-slate-50 p-4 space-y-3"
              data-testid="checkout-fy-block"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 w-4 h-4 bg-[#0A192F]" aria-hidden />
                <div className="text-sm">
                  <div className="font-heading font-bold text-[#0A192F]">
                    Month-to-month · aligned with {fyInfo.fy_label}
                  </div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    Your subscription is monthly — billed at ${plan.price} + GST and you can cancel
                    any time. The scope is aligned with the NZ financial year ({fyInfo.fy_start} →{" "}
                    {fyInfo.fy_end}); we cover the months you stay subscribed within that year.
                    This period does <strong>not</strong> cover {fyInfo.prior_fy_label} compliance —
                    if that return isn't done yet, add the catch-up below.
                  </p>
                </div>
              </div>

              <label
                className="flex items-start gap-3 border border-slate-300 bg-white p-3 cursor-pointer hover:border-[#C9A349] transition-colors"
                htmlFor="prior_year_catchup"
              >
                <Checkbox
                  id="prior_year_catchup"
                  data-testid="checkout-input-catchup"
                  checked={form.prior_year_catchup}
                  onCheckedChange={(v) => update("prior_year_catchup", !!v)}
                  className="mt-0.5 rounded-none border-slate-400 data-[state=checked]:bg-[#C9A349] data-[state=checked]:border-[#C9A349]"
                />
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="font-heading font-bold text-sm">
                      Add {fyInfo.prior_fy_label} catch-up
                    </span>
                    <span className="font-heading font-bold text-sm text-[#C9A349]">
                      + ${plan.price * (fyInfo.catchup_multiplier || 3)} NZD
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Prior-year tax return & financials prepared and filed. One-off, charged once
                    at checkout.
                  </p>
                </div>
              </label>

              <div
                className="border-t border-slate-300 pt-3 text-sm"
                data-testid="checkout-due-today"
              >
                <div className="flex items-center justify-between text-slate-700">
                  <span>First month ({plan.name})</span>
                  <span>${plan.price.toFixed(2)}</span>
                </div>
                {form.prior_year_catchup && (
                  <div className="flex items-center justify-between text-slate-700 mt-1">
                    <span>{fyInfo.prior_fy_label} catch-up</span>
                    <span>
                      ${(plan.price * (fyInfo.catchup_multiplier || 3)).toFixed(2)}
                    </span>
                  </div>
                )}
                {(() => {
                  const subtotal =
                    plan.price +
                    (form.prior_year_catchup
                      ? plan.price * (fyInfo.catchup_multiplier || 3)
                      : 0);
                  const gst = subtotal * 0.15;
                  const total = subtotal + gst;
                  return (
                    <>
                      <div className="flex items-center justify-between text-slate-600 mt-2 pt-2 border-t border-slate-200">
                        <span>Subtotal</span>
                        <span>${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-600 mt-1">
                        <span>GST (15%)</span>
                        <span>${gst.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between font-heading font-bold text-base mt-2 text-[#0A192F]">
                        <span>Due today (incl. GST)</span>
                        <span>${total.toFixed(2)} NZD</span>
                      </div>
                    </>
                  );
                })()}
                <p className="text-[11px] text-slate-500 mt-1">
                  Then ${plan.price} + GST per month for as long as you stay subscribed (cancel any
                  time).
                </p>
              </div>
            </div>
          )}

          <Field label="Anything we should know? (optional)">
            <Textarea
              data-testid="checkout-input-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="rounded-none border-slate-300 focus-visible:ring-[#C9A349] focus-visible:border-[#C9A349] min-h-[80px]"
              placeholder="e.g. Just started trading in Oct, first time with an accountant…"
            />
          </Field>

          <button
            type="submit"
            disabled={submitting || !plan}
            className="w-full bg-[#0A192F] text-white py-4 font-medium flex items-center justify-center gap-2 hover:bg-[#C9A349] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="checkout-form-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…
              </>
            ) : (
              <>
                Continue to secure checkout <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          {/* Manual fallback if browser/extension blocks the auto redirect */}
          {stripeUrl && (
            <a
              href={stripeUrl}
              className="block text-center text-sm text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
              data-testid="checkout-stripe-fallback-link"
            >
              Taking too long? Click here to open Stripe checkout →
            </a>
          )}
          <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
            <Lock className="w-3 h-3" /> Payments secured by Stripe · Cancel any time
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">
        {label}
        {required && <span className="text-[#C9A349]"> *</span>}
      </Label>
      {children}
    </div>
  );
}
